import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map, tap } from 'rxjs';
import { ApiClientService } from '../api/api-client.service';
import { ModeService } from '../mode/mode.service';

const SESSIONS_KEY = 'ss_tenant_sessions';

export type TenantAccountRole = 'Owner' | 'Admin' | 'Coach' | 'Student';
export type TenantPersona = 'Coach' | 'Athlete';

export type ResourceAuthTarget =
  | 'Tenant'
  | 'TenantSettings'
  | 'UserManagement'
  | 'CoachManagement'
  | 'Locations'
  | 'Groups'
  | 'Enrollments'
  | 'Classes'
  | 'Attendance'
  | 'Events'
  | 'TenantPayments'
  | 'StudentManagement'
  | 'Xp'
  | 'Chat'
  | 'Competitions'
  | 'PublicSite'
  | 'Announcements'
  | 'Contracts'
  | 'Billing';
export type AuthorizationLevel = 'Read' | 'Write' | 'Delete';

export interface ResourceAuthorization {
  readonly target: ResourceAuthTarget;
  readonly levels: readonly AuthorizationLevel[];
}

export function personaFromRole(role: TenantAccountRole): TenantPersona {
  return role === 'Student' ? 'Athlete' : 'Coach';
}

export type TenantLoginRole = 'Coach' | 'Student';

export interface TenantLoginDto {
  login: string;
  password: string;
  role: TenantLoginRole;
}

export interface TenantAuthResponse {
  readonly tenantId: string;
  readonly subdomain: string;
  readonly tenantAccountId: string;
  readonly email: string;
  readonly role: TenantAccountRole;
  readonly token: string;
  readonly expiresAt: string;
  readonly authorizations?: readonly ResourceAuthorization[];
}

export type TenantSession = TenantAuthResponse;

type SessionMap = Record<string, TenantSession>;

function sessionKey(tenantId: string, persona: TenantPersona): string {
  return `${tenantId}:${persona}`;
}

@Injectable({ providedIn: 'root' })
export class TenantAuthService {
  private readonly api = inject(ApiClientService);
  private readonly mode = inject(ModeService);
  private readonly router = inject(Router);

  private readonly _sessions = signal<SessionMap>(readStoredSessions());
  private readonly _currentSessionKey = signal<string | null>(null);

  readonly sessions = this._sessions.asReadonly();
  readonly currentSession = computed<TenantSession | null>(() => {
    const key = this._currentSessionKey();
    if (key) return this._sessions()[key] ?? null;
    const sub = this.mode.subdomain();
    if (!sub) return null;
    return findFirstBySubdomain(this._sessions(), sub);
  });
  readonly isAuthenticated = computed(() => {
    const s = this.currentSession();
    return !!s && !isExpired(s);
  });
  readonly token = computed(() => {
    const s = this.currentSession();
    return s && !isExpired(s) ? s.token : null;
  });
  readonly email = computed(() => this.currentSession()?.email ?? null);
  readonly role = computed(() => this.currentSession()?.role ?? null);
  readonly persona = computed<TenantPersona | null>(() => {
    const r = this.role();
    return r ? personaFromRole(r) : null;
  });
  readonly availablePersonas = computed<ReadonlySet<TenantPersona>>(() => {
    const sub = this.mode.subdomain();
    if (!sub) return new Set();
    const out = new Set<TenantPersona>();
    for (const s of Object.values(this._sessions())) {
      if (s.subdomain === sub.toLowerCase() && !isExpired(s)) {
        out.add(personaFromRole(s.role));
      }
    }
    return out;
  });
  readonly authorizations = computed<readonly ResourceAuthorization[]>(
    () => this.currentSession()?.authorizations ?? [],
  );

  hasPermission(target: ResourceAuthTarget, level: AuthorizationLevel): boolean {
    if (this.role() === 'Owner') return true;
    return this.authorizations().some(
      (a) => a.target === target && a.levels.includes(level),
    );
  }

  patchOwnAuthorizations(authorizations: readonly ResourceAuthorization[]): void {
    const s = this.currentSession();
    if (!s) return;
    const key = sessionKey(s.tenantId, personaFromRole(s.role));
    const next = { ...this._sessions(), [key]: { ...s, authorizations } };
    this._sessions.set(next);
    writeStoredSessions(next);
  }

  patchOwnRole(role: TenantAccountRole, authorizations: readonly ResourceAuthorization[]): void {
    const s = this.currentSession();
    if (!s) return;
    const oldKey = sessionKey(s.tenantId, personaFromRole(s.role));
    const newKey = sessionKey(s.tenantId, personaFromRole(role));
    const next: SessionMap = { ...this._sessions() };
    if (oldKey !== newKey) delete next[oldKey];
    next[newKey] = { ...s, role, authorizations };
    this._sessions.set(next);
    if (this._currentSessionKey() === oldKey && oldKey !== newKey) {
      this._currentSessionKey.set(newKey);
    }
    writeStoredSessions(next);
  }

  constructor() {
    this.pruneExpired();
  }

  loginBySubdomain(subdomain: string, body: TenantLoginDto): Observable<TenantAuthResponse> {
    return this.api
      .post<TenantAuthResponse>(`/api/tenants/${encodeURIComponent(subdomain)}/auth`, body)
      .pipe(
        map(normalizeResponse),
        tap((res) => this.persistSession(res)),
      );
  }

  loginByTenantId(tenantId: string, body: TenantLoginDto): Observable<TenantAuthResponse> {
    return this.api
      .post<TenantAuthResponse>(`/api/tenants/${encodeURIComponent(tenantId)}/auth`, body)
      .pipe(
        map(normalizeResponse),
        tap((res) => this.persistSession(res)),
      );
  }

  acceptSession(res: TenantAuthResponse): void {
    this.persistSession(normalizeResponse(res));
  }

  switchPersona(target: TenantPersona): boolean {
    const sub = this.mode.subdomain();
    if (!sub) return false;
    const targetKey = findKeyBySubdomainPersona(this._sessions(), sub, target);
    if (!targetKey) return false;
    this._currentSessionKey.set(targetKey);
    return true;
  }

  logout(): void {
    const current = this.currentSession();
    if (current) {
      this.removeSession(sessionKey(current.tenantId, personaFromRole(current.role)));
    }
    this._currentSessionKey.set(null);

    const sub = this.mode.subdomain();
    const fallback = sub ? findFirstBySubdomain(this._sessions(), sub) : null;
    if (fallback) {
      this._currentSessionKey.set(sessionKey(fallback.tenantId, personaFromRole(fallback.role)));
      this.router.navigateByUrl('/dashboard');
      return;
    }
    this.router.navigateByUrl('/login');
  }

  clearAll(): void {
    this._sessions.set({});
    this._currentSessionKey.set(null);
    writeStoredSessions({});
  }

  private persistSession(res: TenantSession): void {
    const key = sessionKey(res.tenantId, personaFromRole(res.role));
    const next = { ...this._sessions(), [key]: res };
    this._sessions.set(next);
    this._currentSessionKey.set(key);
    writeStoredSessions(next);
  }

  private removeSession(key: string): void {
    const next = { ...this._sessions() };
    delete next[key];
    this._sessions.set(next);
    writeStoredSessions(next);
  }

  private pruneExpired(): void {
    const current = this._sessions();
    let changed = false;
    const next: SessionMap = {};
    for (const [key, s] of Object.entries(current)) {
      if (isExpired(s)) {
        changed = true;
        continue;
      }
      next[key] = s;
    }
    if (changed) {
      this._sessions.set(next);
      writeStoredSessions(next);
    }
  }
}

function normalizeResponse(res: TenantAuthResponse): TenantAuthResponse {
  return {
    tenantId: res.tenantId,
    subdomain: res.subdomain.toLowerCase(),
    tenantAccountId: res.tenantAccountId,
    email: res.email,
    role: res.role,
    token: res.token,
    expiresAt: res.expiresAt,
    authorizations: res.authorizations ?? [],
  };
}

function findFirstBySubdomain(sessions: SessionMap, subdomain: string): TenantSession | null {
  const target = subdomain.toLowerCase();
  for (const s of Object.values(sessions)) {
    if (s.subdomain === target && !isExpired(s)) return s;
  }
  return null;
}

function findKeyBySubdomainPersona(
  sessions: SessionMap,
  subdomain: string,
  persona: TenantPersona,
): string | null {
  const target = subdomain.toLowerCase();
  for (const [key, s] of Object.entries(sessions)) {
    if (s.subdomain === target && !isExpired(s) && personaFromRole(s.role) === persona) {
      return key;
    }
  }
  return null;
}

function isExpired(session: TenantSession): boolean {
  const exp = Date.parse(session.expiresAt);
  if (Number.isNaN(exp)) return false;
  return exp <= Date.now();
}

function readStoredSessions(): SessionMap {
  try {
    const raw = localStorage.getItem(SESSIONS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return migrateLegacyKeys(parsed as SessionMap);
  } catch {
    return {};
  }
}

function migrateLegacyKeys(stored: SessionMap): SessionMap {
  const out: SessionMap = {};
  let migrated = false;
  for (const [key, value] of Object.entries(stored)) {
    if (!value || typeof value !== 'object' || !('role' in value)) continue;
    const expectedKey = sessionKey(value.tenantId, personaFromRole(value.role));
    if (key !== expectedKey) migrated = true;
    out[expectedKey] = value;
  }
  if (migrated) writeStoredSessions(out);
  return out;
}

function writeStoredSessions(sessions: SessionMap): void {
  try {
    if (Object.keys(sessions).length === 0) {
      localStorage.removeItem(SESSIONS_KEY);
      return;
    }
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
  } catch {
    // storage disabled — signals still hold state for the session
  }
}
